import React, { useState, useEffect, useCallback, useMemo } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import { isSmallShopMode } from '../../utils/smallShopMode'
import jsPDF from 'jspdf'
import Swal from 'sweetalert2'
import 'jspdf-autotable'

const LowStock = () => {
  const smallShopMode = isSmallShopMode()
  const locationLabel = smallShopMode ? 'Branch' : 'Warehouse'
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  const fetchLowStock = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const endpoint = smallShopMode ? API_ENDPOINTS.BRANCH_LOW_STOCK : API_ENDPOINTS.LOW_STOCK
      const response = await apiClient.get(endpoint)
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.results || []
      setItems(data)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error fetching low stock items:', err)
      setItems([])
      setError(err.response?.data?.detail || err.response?.data?.message || 'Failed to load low stock items. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [smallShopMode])

  useEffect(() => {
    fetchLowStock()
  }, [fetchLowStock])

  const getLocationName = (item) => {
    if (smallShopMode) {
      return item.branch_name || 'N/A'
    }
    return item.warehouse_name || 'N/A'
  }

  const getItemKey = (item) => {
    if (smallShopMode) {
      return `${item.product_id}-${item.branch_id}`
    }
    return `${item.product_id}-${item.warehouse_id}`
  }

  const filteredItems = useMemo(() => {
    if (!search) return items
    const term = search.toLowerCase()
    return items.filter((item) => (
      item.product_name?.toLowerCase().includes(term) ||
      item.product_sku?.toLowerCase().includes(term) ||
      getLocationName(item)?.toLowerCase().includes(term)
    ))
  }, [items, search, smallShopMode])

  const formatNumber = (value) => {
    const parsed = parseFloat(value)
    if (Number.isNaN(parsed)) {
      return value ?? '-'
    }
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  }

  const isBelowReorder = (item) => {
    const quantity = parseFloat(item.quantity) || 0
    const reorder = parseFloat(item.reorder_level) || 0
    if (reorder === 0) return false
    return quantity < reorder
  }

  const totalBelowReorder = useMemo(() => filteredItems.filter(isBelowReorder).length, [filteredItems])

  const generatePDF = () => {
    if (filteredItems.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There are no low stock items to export. Please adjust your filters.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      const doc = new jsPDF('portrait', 'mm', 'a4')
      const margin = 14
      let yPos = margin

      doc.setFontSize(18)
      doc.setTextColor(40, 40, 40)
      doc.text('Low Stock Report', margin, yPos)
      yPos += 8

      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      const filtersApplied = []
      if (search) filtersApplied.push(`Search: ${search}`)

      if (filtersApplied.length > 0) {
        doc.text(`Filters Applied: ${filtersApplied.join(' | ')}`, margin, yPos)
        yPos += 5
      }

      doc.text(`Generated: ${new Date().toLocaleString()} | Total Records: ${filteredItems.length}`, margin, yPos)
      doc.text(`Total Alerts: ${items.length} | Below Reorder: ${totalBelowReorder}`, margin, yPos + 5)
      yPos += 12

      const tableData = filteredItems.map(item => {
        const below = isBelowReorder(item)
        return [
          item.product_name || 'N/A',
          item.product_sku || 'N/A',
          getLocationName(item),
          formatNumber(item.quantity),
          formatNumber(item.reorder_level),
          below ? 'Below Reorder' : 'Above Reorder'
        ]
      })

      doc.autoTable({
        startY: yPos,
        head: [['Product', 'SKU', locationLabel, 'Quantity', 'Reorder Level', 'Status']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [220, 53, 69],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 30 },
          2: { cellWidth: 40 },
          3: { cellWidth: 25, halign: 'center' },
          4: { cellWidth: 30, halign: 'center' },
          5: { cellWidth: 35, halign: 'center' }
        },
        didParseCell: function (data) {
          if (data.column.index === 5 && data.cell.text[0] === 'Below Reorder') {
            data.cell.styles.fillColor = [255, 193, 7]
            data.cell.styles.textColor = [0, 0, 0]
          }
        },
        margin: { top: yPos, left: margin, right: margin }
      })

      const fileName = `low-stock-report-${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'Low stock report has been downloaded successfully.',
        confirmButtonColor: '#3085d6',
        timer: 2000,
        showConfirmButton: false
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Low Stock</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Stocks</li>
            <li className="breadcrumb-item active">Low Stock</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 py-3 border-bottom mb-3">
                  <div>
                    <h5 className="card-title mb-1">
                      <i className="bi bi-exclamation-triangle me-2 text-warning"></i>
                      Low Stock Alerts
                    </h5>
                    <p className="text-muted mb-0">
                      {smallShopMode
                        ? 'Track products that are below their reorder level across branches.'
                        : 'Track products that are below their reorder level across warehouses.'}
                    </p>
                  </div>
                  <div className="text-md-end">
                    {lastUpdated && (
                      <small className="text-muted d-block mb-1">
                        Last updated: {lastUpdated.toLocaleString()}
                      </small>
                    )}
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={generatePDF}
                        disabled={loading || filteredItems.length === 0}
                      >
                        <i className="bi bi-file-pdf me-1"></i>
                        Download PDF
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={fetchLowStock}
                        disabled={loading}
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <div className="info-card h-100">
                      <div className="card-body">
                        <h6>Total Alerts</h6>
                        <div className="d-flex align-items-center">
                          <div className="icon text-primary bg-light rounded-circle me-3 p-3">
                            <i className="bi bi-bell"></i>
                          </div>
                          <div>
                            <h3 className="mb-0">{items.length}</h3>
                            <small className="text-muted">Products monitored</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-card h-100">
                      <div className="card-body">
                        <h6>Below Reorder</h6>
                        <div className="d-flex align-items-center">
                          <div className="icon text-danger bg-light rounded-circle me-3 p-3">
                            <i className="bi bi-graph-down"></i>
                          </div>
                          <div>
                            <h3 className="mb-0 text-danger">{totalBelowReorder}</h3>
                            <small className="text-muted">Require urgent attention</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-card h-100">
                      <div className="card-body">
                        <h6>Search</h6>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={`Search by product, SKU or ${locationLabel.toLowerCase()}`}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger d-flex align-items-center" role="alert">
                    <i className="bi bi-exclamation-octagon me-2"></i>
                    <div>{error}</div>
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Fetching low stock items...</p>
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-check-circle" style={{ fontSize: '3rem', color: '#0d6efd' }}></i>
                    <p className="mt-3 text-muted">No products found for the current filters.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th>{locationLabel}</th>
                          <th className="text-center">Quantity</th>
                          <th className="text-center">Reorder Level</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map((item) => {
                          const below = isBelowReorder(item)
                          return (
                            <tr key={getItemKey(item)}>
                              <td>
                                <div className="fw-semibold">{item.product_name || 'N/A'}</div>
                              </td>
                              <td>
                                <span className="badge bg-secondary">{item.product_sku || 'N/A'}</span>
                              </td>
                              <td>{getLocationName(item)}</td>
                              <td className="text-center fw-semibold">{formatNumber(item.quantity)}</td>
                              <td className="text-center">{formatNumber(item.reorder_level)}</td>
                              <td className="text-center">
                                <span className={`badge ${below ? 'bg-danger' : 'bg-success'}`}>
                                  {below ? 'Below Reorder' : 'Above Reorder'}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {!loading && (
                  <div className="mt-3 text-muted">
                    <small>
                      Showing <strong>{filteredItems.length}</strong> of <strong>{items.length}</strong> products
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default LowStock
